import type { DotLottieWorker as DotLottieWorkerType } from "@lottiefiles/dotlottie-web"
import { loadPlayer } from "./dotlottie_player"

export class HowItWorksAnimation {
  dotLottie: DotLottieWorkerType | null = null
  observer: IntersectionObserver | null = null
  container: HTMLElement
  playerElement: HTMLElement | null
  loaded: boolean = false
  visible: boolean = false

  constructor(container: HTMLElement) {
    this.container = container
    this.playerElement = container.querySelector("[data-lottie-player]")

    if (!this.playerElement) {
      console.error("Lottie player element not found")
      return
    }

    this.setupIntersectionObserver()
    this.setupPlaybackObserver()
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: "100px 0px",
      threshold: 0,
    }

    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.loaded) {
          this.loadAnimation()
          this.loaded = true
          this.observer?.disconnect()
        }
      })
    }, options)

    this.observer.observe(this.container)
  }

  // Playback follows visibility rather than loading, so an animation parsed ahead of the
  // reader is still on its opening frame when they arrive, and picks up where it left off
  // if they scroll away and come back.
  setupPlaybackObserver() {
    // A fifth of the box, so the loop does not start while the animation is a sliver at the
    // bottom edge — the reader would then arrive to the middle of it.
    const options = { threshold: 0.2 }

    new IntersectionObserver(entries => {
      entries.forEach(entry => {
        this.visible = entry.isIntersecting

        if (entry.isIntersecting) {
          this.dotLottie?.play()
        } else {
          this.dotLottie?.pause()
        }
      })
    }, options).observe(this.container)
  }

  showFallbackFrame() {
    this.playerElement?.classList.add("has-failed")
  }

  async loadAnimation() {
    if (!this.playerElement) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    try {
      const [{ DotLottieWorker }, { default: animationUrl }] = await Promise.all([
        loadPlayer(),
        import("images/home/prosumer/if-then.lottie?url"),
      ])

      const canvas = document.createElement("canvas")
      canvas.style.width = "100%"
      canvas.style.height = "100%"
      this.playerElement.appendChild(canvas)

      this.dotLottie = new DotLottieWorker({
        canvas,
        src: animationUrl,
        workerId: "dotlottie-shared",
        loop: true,
        autoplay: false,
      })

      this.dotLottie.addEventListener("load", () => {
        if (this.visible) this.dotLottie?.play()
      })

      // The asset is fetched inside the worker, so a failure there arrives as an event rather
      // than rejecting the call above. Either way the box would sit empty, so show the frame.
      this.dotLottie.addEventListener("loadError", () => this.showFallbackFrame())

      if (window.App?.Utils?.logCustomDatadogAction) {
        window.App.Utils.logCustomDatadogAction("how_it_works_animation_loaded", {
          platform: window.innerWidth <= 768 ? "mweb" : "web",
        })
      }
    } catch (error) {
      console.error("Failed to load dotLottie animation:", error)
      this.showFallbackFrame()
    }
  }
}

const initHowItWorksAnimation = () => {
  const container = document.querySelector("[data-animation-container]") as HTMLElement
  if (container) {
    new HowItWorksAnimation(container)
  }
}

// Waiting for `load` would hold the observer back until the hero collage and the fonts are
// done, which is most of the time a reader spends before the first scroll.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHowItWorksAnimation, { once: true })
} else {
  initHowItWorksAnimation()
}
